/**
 * Checkout / thank-you / post-purchase extension preview helpers.
 * Shared by weupsell-preview editor iframe and standalone preview entry points.
 */
import { isStaticCustomCartDrawer } from "./custom-cart-drawer-template.js";
import { isStaticProductPageUpsell } from "./product-page-upsell-template.js";
import { isStaticVolumeDiscount } from "./volume-discount-template.js";

export const EXTENSION_PAGE = Object.freeze({
  CHECKOUT: "CHECKOUT",
  POST_PURCHASE: "POST_PURCHASE",
  THANK_YOU_PAGE: "THANK_YOU_PAGE",
  ORDER_STATUS_PAGE: "ORDER_STATUS_PAGE",
  CART_DRAWER: "CART_DRAWER",
});

export const EXTENSION_PAGE_LABELS = Object.freeze({
  [EXTENSION_PAGE.CHECKOUT]: "Checkout page",
  [EXTENSION_PAGE.POST_PURCHASE]: "Post purchase page",
  [EXTENSION_PAGE.THANK_YOU_PAGE]: "Thank you page",
  [EXTENSION_PAGE.ORDER_STATUS_PAGE]: "Order status page",
  [EXTENSION_PAGE.CART_DRAWER]: "Cart drawer",
});

const POPUP_TYPE_TO_PAGE = Object.freeze({
  checkout_extension_upsell_widget: EXTENSION_PAGE.CHECKOUT,
  post_purchase_widget: EXTENSION_PAGE.POST_PURCHASE,
  thankyou_widget: EXTENSION_PAGE.THANK_YOU_PAGE,
  order_status_widget: EXTENSION_PAGE.ORDER_STATUS_PAGE,
  cart_drawer_widget: EXTENSION_PAGE.CART_DRAWER,
});

function getPopupSearchText(popupValues) {
  return [
    popupValues?.popUpType,
    popupValues?.template_type,
    popupValues?.title,
    popupValues?.generalSettings?.title,
    popupValues?.template_id?.title,
    popupValues?.category?.primary,
    popupValues?.category?.label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isCartProductOfferPopup(popupValues) {
  const text = getPopupSearchText(popupValues);
  return (
    text.includes("cart product offer") ||
    (text.includes("cart") && text.includes("product recommendation"))
  );
}

export function getExtensionPageForPopup(popupValues) {
  if (isStaticCustomCartDrawer(popupValues)) return null;

  const text = getPopupSearchText(popupValues);
  if (!text && !POPUP_TYPE_TO_PAGE[popupValues?.popUpType]) return null;

  if (isCartProductOfferPopup(popupValues)) return EXTENSION_PAGE.CART_DRAWER;
  if (text.includes("cart drawer")) return EXTENSION_PAGE.CART_DRAWER;
  if (text.includes("order status")) return EXTENSION_PAGE.ORDER_STATUS_PAGE;
  if (text.includes("thank")) return EXTENSION_PAGE.THANK_YOU_PAGE;
  if (text.includes("post purchase") || text.includes("post-purchase")) {
    return EXTENSION_PAGE.POST_PURCHASE;
  }

  if (POPUP_TYPE_TO_PAGE[popupValues?.popUpType]) {
    return POPUP_TYPE_TO_PAGE[popupValues.popUpType];
  }

  if (text.includes("checkout")) return EXTENSION_PAGE.CHECKOUT;

  return null;
}

export function isCheckoutExtensionsCategory(popupValues) {
  const primary = String(popupValues?.category?.primary || "").toLowerCase();
  const key = String(popupValues?.category?.key || "").toLowerCase();
  return (
    primary.includes("checkout extension") ||
    primary.includes("upsell extension") ||
    key === "checkout-extensions"
  );
}

export function isExtensionPopup(popupValues) {
  return (
    Boolean(getExtensionPageForPopup(popupValues)) ||
    isCheckoutExtensionsCategory(popupValues)
  );
}

/** Checkout / thank-you / order-status / post-purchase — Shopify-hosted only, not storefront hybrid. */
const CHECKOUT_HOSTED_EXTENSION_SURFACES = new Set([
  EXTENSION_PAGE.CHECKOUT,
  EXTENSION_PAGE.POST_PURCHASE,
  EXTENSION_PAGE.THANK_YOU_PAGE,
  EXTENSION_PAGE.ORDER_STATUS_PAGE,
]);

export function isCheckoutHostedExtensionPopup(popupValues) {
  if (!popupValues || typeof popupValues !== "object") return false;
  if (isStaticCustomCartDrawer(popupValues)) return false;
  if (isStaticProductPageUpsell(popupValues)) return false;

  const surface = getExtensionPageForPopup(popupValues);
  if (surface && CHECKOUT_HOSTED_EXTENSION_SURFACES.has(surface)) {
    return true;
  }

  if (!isCheckoutExtensionsCategory(popupValues)) return false;
  if (isCartProductOfferPopup(popupValues)) return false;
  if (surface === EXTENSION_PAGE.CART_DRAWER) return false;

  return true;
}

/**
 * Prefer explicit surface from editor postMessage; fall back to popup metadata.
 * @param {object | null | undefined} popupValues
 * @param {string | null | undefined} explicitSurface
 * @returns {string | null}
 */
export function resolveExtensionSurface(popupValues, explicitSurface) {
  if (
    explicitSurface &&
    Object.prototype.hasOwnProperty.call(EXTENSION_PAGE_LABELS, explicitSurface)
  ) {
    return explicitSurface;
  }

  if (!popupValues) return null;
  if (isStaticCustomCartDrawer(popupValues)) return null;

  const resolved = getExtensionPageForPopup(popupValues);
  if (resolved) return resolved;

  if (isCheckoutExtensionsCategory(popupValues)) {
    return EXTENSION_PAGE.CHECKOUT;
  }

  return null;
}

export function getExtensionPageLabel(surface) {
  return EXTENSION_PAGE_LABELS[surface] || "Extension page";
}

/** Editor iframe uses dedicated surface previews (not storefront popup overlay). */
export const EXTENSION_EDITOR_SURFACE_PREVIEWS = Object.freeze([
  EXTENSION_PAGE.CHECKOUT,
  EXTENSION_PAGE.POST_PURCHASE,
  EXTENSION_PAGE.THANK_YOU_PAGE,
  EXTENSION_PAGE.ORDER_STATUS_PAGE,
  EXTENSION_PAGE.CART_DRAWER,
]);

export function isExtensionEditorSurfacePreview(surface) {
  return (
    Boolean(surface) &&
    EXTENSION_EDITOR_SURFACE_PREVIEWS.includes(surface)
  );
}

/** Cart drawer widget uses full popup theme/CSS — not Shopify surface presets. */
export function extensionSurfaceUsesThemeSettings(surface) {
  return surface === EXTENSION_PAGE.CART_DRAWER;
}

/** Editor iframe: full-page templates render full-frame, not in embed placeholder slots. */
export function shouldBypassEmbedPortalInEditorPreview(popupValues, appState) {
  if (!appState?.is_preview_mode) return false;
  if (isStaticProductPageUpsell(popupValues)) return true;
  if (isStaticVolumeDiscount(popupValues)) return true;
  if (appState?.previewChrome === "widget") return true;
  const surface = resolveExtensionSurface(
    popupValues,
    appState?.extensionSurface
  );
  return isExtensionEditorSurfacePreview(surface);
}
